import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { setAppOrgCode } from '../components/updateApp/appStatusSlice';
import type { Org } from '../helpers/device.helper';
import { getDidOnboarding, removeCookies } from '../helpers/device.helper';
import { fetchGuestInfo, getAreas, resetPreferences, saveArea } from '../pages/guest/guestSlice';
import { useAppDispatch, useAppSelector } from '../store';

export const OrgCodeProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const firstAreaOrgCode = useAppSelector((state) => state.guestSlice.areas[0]?.code);
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    document.getElementById('loader')?.remove();
    removeCookies();
  }, []);

  useEffect(() => {
    dispatch(getAreas());
  }, [dispatch]);

  const setupApp = useCallback(
    (orgCode: Org) => {
      dispatch(setAppOrgCode(orgCode));
      setIsAppReady(true);
    },
    [dispatch],
  );

  const retrieveGuestInfo = useCallback(async () => {
    const response = await dispatch(fetchGuestInfo());
    if (fetchGuestInfo.fulfilled.match(response) && response.payload) {
      const guestInfo = response.payload;
      setupApp(guestInfo.org_code);
    }
  }, [dispatch, setupApp]);

  const setFirstArea = useCallback(async () => {
    if (firstAreaOrgCode) {
      await dispatch(saveArea({ new_org_code: firstAreaOrgCode }));
      setupApp(firstAreaOrgCode);
    }
  }, [dispatch, firstAreaOrgCode, setupApp]);

  useEffect(() => {
    if (getDidOnboarding()) {
      // oxlint-disable-next-line react/set-state-in-effect -- app bootstrap: fetches guest info from an external API, setIsAppReady only runs after the awaited dispatch resolves
      retrieveGuestInfo();
    } else {
      dispatch(resetPreferences());
      setFirstArea();
    }
  }, [dispatch, retrieveGuestInfo, setFirstArea]);

  if (!isAppReady) {
    return null;
  }

  return <>{children}</>;
};
